import { useEffect } from "react";

function getAllowedParentOrigins(): string[] {
  const raw: string = import.meta.env.VITE_ALLOWED_PARENT_ORIGINS ?? "";
  return raw.split(",").map((origin) => origin.trim()).filter(Boolean);
}

function isIframed(): boolean {
  return typeof window !== "undefined" && window.self !== window.top;
}

const AUTH_REQUEST_TYPE = "sapps:auth-request";
const AUTH_REFRESH_REQUEST_TYPE = "sapps:auth-token-refresh";
const AUTH_TOKEN_TYPE = "sapps:auth-token";
const SESSION_ENDED_TYPE = "sapps:session-ended";

interface ParentAuthTokenRequestOptions {
  readonly timeoutMs?: number;
  readonly forceRefresh?: boolean;
}

const DEFAULT_PARENT_AUTH_TIMEOUT_MS = 5_000;

type WireKind = "ordinary" | "refresh";
type RelayStatus = "active" | "ended" | "desynchronized";

interface ParentAuthWaiter { readonly resolve: (token: string | null) => void }

interface ParentAuthTransaction {
  readonly waiters: ParentAuthWaiter[];
  wireKind: WireKind;
  refreshRequired: boolean;
  refreshTimeoutMs: number;
  timeoutId: ReturnType<typeof setTimeout> | null;
}

let activeTransaction: ParentAuthTransaction | null = null;
let relayStatus: RelayStatus = "active";
const sessionEndedSubscribers = new Set<() => void>();
let isParentAuthMessageListenerActive = false;
let hasLoggedInvalidParentToken = false;

function postParentAuthWireRequest(forceRefresh = false): void {
  window.parent.postMessage({ type: forceRefresh ? AUTH_REFRESH_REQUEST_TYPE : AUTH_REQUEST_TYPE }, "*");
}

function isParentAuthTokenMessage(data: unknown): data is { type: typeof AUTH_TOKEN_TYPE; token: string } {
  return (
    typeof data === "object" &&
    data !== null &&
    "type" in data && data.type === AUTH_TOKEN_TYPE &&
    "token" in data && typeof data.token === "string"
  );
}

function isParentSessionEndedMessage(data: unknown): data is { type: typeof SESSION_ENDED_TYPE } {
  return (
    typeof data === "object" &&
    data !== null &&
    "type" in data &&
    data.type === SESSION_ENDED_TYPE
  );
}

function isJwtToken(token: string): boolean {
  const segments = token.split(".");
  return segments.length === 3 && segments.every((segment) => segment.length > 0);
}

function getValidParentToken(data: unknown): string | null {
  if (!isParentAuthTokenMessage(data)) {
    return null;
  }
  if (!isJwtToken(data.token)) {
    if (!hasLoggedInvalidParentToken) {
      hasLoggedInvalidParentToken = true;
      console.error("[Frontend][Auth] Parent relayed non-JWT token — ignoring", {
        tokenLength: data.token.length,
      });
    }
    return null;
  }
  return data.token;
}

function clearWireTimeout(transaction: ParentAuthTransaction): void {
  if (transaction.timeoutId === null) {
    return;
  }
  clearTimeout(transaction.timeoutId);
  transaction.timeoutId = null;
}

function removeParentListenerWhenIdle(): void {
  if (activeTransaction === null && sessionEndedSubscribers.size === 0 && isParentAuthMessageListenerActive) {
    window.removeEventListener("message", handleParentAuthMessage);
    isParentAuthMessageListenerActive = false;
  }
}

function settleActiveTransaction(token: string | null): void {
  const transaction = activeTransaction;
  if (transaction === null) {
    return;
  }
  clearWireTimeout(transaction);
  activeTransaction = null;
  for (const waiter of transaction.waiters) {
    waiter.resolve(token);
  }
  removeParentListenerWhenIdle();
}

function latchRelay(status: Exclude<RelayStatus, "active">): void {
  if (relayStatus !== "active") {
    return;
  }
  relayStatus = status;
  settleActiveTransaction(null);
  sessionEndedSubscribers.clear();
  if (isParentAuthMessageListenerActive) {
    window.removeEventListener("message", handleParentAuthMessage);
    isParentAuthMessageListenerActive = false;
  }
}

function startWireRequest(transaction: ParentAuthTransaction, wireKind: WireKind, timeoutMs: number): void {
  transaction.wireKind = wireKind;
  transaction.timeoutId = setTimeout(() => {
    if (activeTransaction === transaction && transaction.wireKind === wireKind) {
      latchRelay("desynchronized");
    }
  }, timeoutMs);
  try {
    postParentAuthWireRequest(wireKind === "refresh");
  } catch (error: unknown) {
    console.error("[Frontend][Auth] Failed to post parent auth relay request", {
      errorName: error instanceof Error ? error.name : "UnknownError",
      wireKind,
    });
    latchRelay("desynchronized");
  }
}

function ensureParentAuthMessageListener(): void {
  if (isParentAuthMessageListenerActive || relayStatus !== "active") {
    return;
  }
  window.addEventListener("message", handleParentAuthMessage);
  isParentAuthMessageListenerActive = true;
}

function handleParentAuthMessage(event: MessageEvent): void {
  if (relayStatus !== "active" || !getAllowedParentOrigins().includes(event.origin) || event.source !== window.parent) {
    return;
  }
  if (isParentSessionEndedMessage(event.data)) {
    const subscribers = [...sessionEndedSubscribers];
    latchRelay("ended");
    for (const onSessionEnded of subscribers) {
      onSessionEnded();
    }
    return;
  }
  const token = getValidParentToken(event.data);
  const transaction = activeTransaction;
  if (!token || transaction === null) {
    return;
  }
  if (transaction.wireKind === "ordinary" && transaction.refreshRequired) {
    clearWireTimeout(transaction);
    transaction.refreshRequired = false;
    startWireRequest(transaction, "refresh", transaction.refreshTimeoutMs);
    return;
  }
  settleActiveTransaction(token);
}

// One shared transaction serializes the request-ID-free parent protocol. A
// refresh upgrade drains the ordinary response before sending the refresh.
export function requestParentAuthToken(options: ParentAuthTokenRequestOptions = {}): Promise<string | null> {
  if (!isIframed() || relayStatus !== "active") {
    return Promise.resolve(null);
  }

  const allowedOrigins = getAllowedParentOrigins();
  if (allowedOrigins.length === 0) {
    return Promise.resolve(null);
  }

  const timeoutMs = options.timeoutMs ?? DEFAULT_PARENT_AUTH_TIMEOUT_MS;
  const forceRefresh = options.forceRefresh ?? false;
  let resolveWaiter: (token: string | null) => void = () => undefined;
  const waiterPromise = new Promise<string | null>((resolve) => {
    resolveWaiter = resolve;
  });
  if (activeTransaction !== null) {
    if (forceRefresh && activeTransaction.wireKind === "ordinary") {
      activeTransaction.refreshRequired = true;
      activeTransaction.refreshTimeoutMs = timeoutMs;
    }
    activeTransaction.waiters.push({ resolve: resolveWaiter });
    return waiterPromise;
  }

  const transaction: ParentAuthTransaction = {
    waiters: [{ resolve: resolveWaiter }],
    wireKind: forceRefresh ? "refresh" : "ordinary",
    refreshRequired: false,
    refreshTimeoutMs: timeoutMs,
    timeoutId: null,
  };
  activeTransaction = transaction;
  ensureParentAuthMessageListener();
  startWireRequest(transaction, transaction.wireKind, timeoutMs);
  return waiterPromise;
}

export function useParentTokenAuth(
  onToken: (token: string) => void,
  onConfigurationFailure?: () => void,
  onSessionEnded?: () => void,
): void {
  useEffect(() => {
    if (!isIframed()) {
      return;
    }
    const allowedOrigins = getAllowedParentOrigins();
    if (allowedOrigins.length === 0) {
      console.warn("[Frontend][Auth] Parent auth relay unavailable because no allowed parent origins are configured");
      onConfigurationFailure?.();
      return;
    }
    if (relayStatus === "ended") {
      onSessionEnded?.();
      return;
    }

    let isActive = true;
    if (onSessionEnded) {
      sessionEndedSubscribers.add(onSessionEnded);
    }
    ensureParentAuthMessageListener();
    void requestParentAuthToken().then((token) => {
      if (isActive && token) {
        onToken(token);
      } else if (isActive && relayStatus !== "ended") {
        onConfigurationFailure?.();
      }
    });

    return () => {
      isActive = false;
      if (onSessionEnded) {
        sessionEndedSubscribers.delete(onSessionEnded);
      }
      removeParentListenerWhenIdle();
    };
  }, [onConfigurationFailure, onSessionEnded, onToken]);
}
